import React, { useState, useEffect } from 'react';
import workCaseApi from '../../services/workCaseApi';

const WorkCaseStatistics = ({ viewMode = 'assigned' }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  // Load statistics
  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await workCaseApi.getPersonalStats({ period });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load stats when component mounts or period changes
  useEffect(() => {
    loadStats();
  }, [period]);

  // Calculate completion rate
  const getCompletionRate = (assigned) => {
    if (!assigned || assigned.total_assigned === 0) return 0;
    return ((assigned.completed || 0) / assigned.total_assigned * 100).toFixed(1);
  };

  // Calculate average completion time
  const getAverageTime = (hours) => {
    // Convert to number and check if it's valid
    const hoursNum = parseFloat(hours);
    if (!hoursNum || isNaN(hoursNum) || hoursNum === 0) return 'N/A';
    if (hoursNum < 24) return `${hoursNum.toFixed(1)} giờ`;
    const days = (hoursNum / 24).toFixed(1);
    return `${days} ngày`;
  };

  // Get period label
  const getPeriodLabel = (period) => {
    switch (period) {
      case 'month': return 'Tháng này';
      case 'quarter': return '3 tháng qua';
      case 'year': return 'Năm nay';
      default: return period;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-500 text-center">Không thể tải thống kê</p>
      </div>
    );
  }

  const isAssignedView = viewMode !== 'created';
  const completedLabel = isAssignedView ? 'Chờ duyệt' : 'Hoàn thành';

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">{isAssignedView ? 'Thống kê cá nhân' : 'Thống kê case tôi đã giao'}</h3>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="month">Tháng này</option>
            <option value="quarter">3 tháng qua</option>
            <option value="year">Năm nay</option>
          </select>
        </div>
        <p className="text-sm text-gray-500 mt-1">{getPeriodLabel(period)}</p>
      </div>

      {/* Statistics */}
      <div className="p-6 space-y-6">
        {/* Quick KPIs */}
        {stats.quick && (
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-indigo-50 rounded-lg p-3 text-center">
              <div className="text-xs text-indigo-600">Được giao hôm nay</div>
              <div className="text-2xl font-bold text-indigo-700">{stats.quick.assignedToday}</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 text-center">
              <div className="text-xs text-orange-600">Sắp hết hạn (được giao)</div>
              <div className="text-2xl font-bold text-orange-700">{stats.quick.dueSoonAssigned}</div>
            </div>
            <div className="bg-rose-50 rounded-lg p-3 text-center">
              <div className="text-xs text-rose-600">Sắp hết hạn (đã giao)</div>
              <div className="text-2xl font-bold text-rose-700">{stats.quick.dueSoonCreated}</div>
            </div>
          </div>
        )}

        {/* Summary cards */}
        {isAssignedView ? (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Cases được giao</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-blue-600">{stats.assigned?.total_assigned || 0}</div>
                <div className="text-xs text-blue-600">Tổng số</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-green-600">{stats.assigned?.completed || 0}</div>
                <div className="text-xs text-green-600">{completedLabel}</div>
              </div>
            </div>
            <div className="mt-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tỷ lệ hoàn thành:</span>
                <span className="font-medium">{getCompletionRate(stats.assigned)}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Thời gian trung bình:</span>
                <span className="font-medium">{getAverageTime(stats.assigned?.avg_completion_hours)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Cases tôi đã giao</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-purple-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-purple-600">{stats.created?.total_created || 0}</div>
                <div className="text-xs text-purple-600">Tổng số</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-emerald-600">{stats.created?.approved || 0}</div>
                <div className="text-xs text-emerald-600">Đã duyệt</div>
              </div>
            </div>
          </div>
        )}

        {/* Cases Created */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Cases đã tạo</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-purple-600">
                {stats.created?.total_created || 0}
              </div>
              <div className="text-xs text-purple-600">Tổng số</div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-emerald-600">
                {stats.created?.approved || 0}
              </div>
              <div className="text-xs text-emerald-600">Đã duyệt</div>
            </div>
          </div>
        </div>

        {/* Status Breakdown */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Phân bố trạng thái</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <span className="text-sm text-gray-600">Chờ xử lý</span>
              </div>
              <span className="text-sm font-medium">{(isAssignedView ? stats.assigned?.pending : stats.created?.pending) || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                <span className="text-sm text-gray-600">Đã nhận</span>
              </div>
              <span className="text-sm font-medium">{(isAssignedView ? stats.assigned?.accepted : stats.created?.accepted) || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
                <span className="text-sm text-gray-600">Đang thực hiện</span>
              </div>
              <span className="text-sm font-medium">{(isAssignedView ? stats.assigned?.in_progress : stats.created?.in_progress) || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                <span className="text-sm text-gray-600">{completedLabel}</span>
              </div>
              <span className="text-sm font-medium">{(isAssignedView ? stats.assigned?.completed : stats.created?.completed) || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <span className="text-sm text-gray-600">Từ chối</span>
              </div>
              <span className="text-sm font-medium">{(isAssignedView ? stats.assigned?.rejected : stats.created?.rejected) || 0}</span>
            </div>
          </div>
        </div>

        {/* Category Breakdown - cá nhân */}
        {isAssignedView && stats.byCategory && stats.byCategory.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Theo danh mục</h4>
            <div className="space-y-2">
              {stats.byCategory.map((category) => (
                <div key={category.category} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 capitalize">{category.category}</span>
                  <span className="text-sm font-medium">{category.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Priority Breakdown - cá nhân */}
        {isAssignedView && stats.byPriority && stats.byPriority.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Theo độ ưu tiên</h4>
            <div className="space-y-2">
              {stats.byPriority.map((priority) => (
                <div key={priority.priority} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 capitalize">{priority.priority}</span>
                  <span className="text-sm font-medium">{priority.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Deadlines - được giao */}
        {isAssignedView && stats.upcomingDeadlines && stats.upcomingDeadlines.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Deadline sắp tới</h4>
            <div className="space-y-2">
              {stats.upcomingDeadlines.slice(0, 3).map((deadline) => (
                <div key={deadline.id} className="bg-red-50 rounded-lg p-2">
                  <div className="text-sm font-medium text-red-800 truncate">
                    {deadline.title}
                  </div>
                  <div className="text-xs text-red-600">
                    {new Date(deadline.deadline).toLocaleDateString('vi-VN')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* View: tôi đã giao */}
        {!isAssignedView && (
          <>
            {stats.byCategoryCreated && stats.byCategoryCreated.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Theo danh mục</h4>
                <div className="space-y-2">
                  {stats.byCategoryCreated.map((category) => (
                    <div key={category.category} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 capitalize">{category.category}</span>
                      <span className="text-sm font-medium">{category.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {stats.byPriorityCreated && stats.byPriorityCreated.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Theo độ ưu tiên</h4>
                <div className="space-y-2">
                  {stats.byPriorityCreated.map((p) => (
                    <div key={p.priority} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 capitalize">{p.priority}</span>
                      <span className="text-sm font-medium">{p.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {stats.upcomingDeadlinesCreated && stats.upcomingDeadlinesCreated.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Deadline sắp tới</h4>
                <div className="space-y-2">
                  {stats.upcomingDeadlinesCreated.slice(0, 3).map((deadline) => (
                    <div key={deadline.id} className="bg-orange-50 rounded-lg p-2">
                      <div className="text-sm font-medium text-orange-800 truncate">{deadline.title}</div>
                      <div className="text-xs text-orange-600">{new Date(deadline.deadline).toLocaleDateString('vi-VN')}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Performance Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Tóm tắt hiệu suất</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Tỷ lệ hoàn thành:</span>
              <span className="font-medium text-green-600">{getCompletionRate(stats.assigned)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Thời gian trung bình:</span>
              <span className="font-medium">{getAverageTime(stats.assigned?.avg_completion_hours)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Cases đang thực hiện:</span>
              <span className="font-medium text-orange-600">{stats.assigned?.in_progress || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkCaseStatistics;
